import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CommandPayload } from "src/data/commands";

import { Device, Group } from "@dashboard/devices/types";
import { JSONEditor as JsonEditorType } from "@json-editor/json-editor/dist/jsoneditor.js";
import { showErrorToast } from "@shared/toasts/Toasts";

import { JsonEditorCommandType, updateEditor, validateEditor } from "../helpers/jsonEditor";
import { usePolicyCommands } from "../hooks";
import { InputItemContainer, useStyles } from "../styles";

let rawEditor: JsonEditorType | null = null;

type Props = {
    device?: Device;
    group?: Group;
    editorRef: React.RefObject<HTMLDivElement | null>;
    submitRef: React.RefObject<HTMLButtonElement | null>;
    isPolicy?: boolean;
    toggleDialog: () => void;
    createCommand?: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
};

export const JsonEditor = ({ group, editorRef, submitRef, isPolicy, toggleDialog, createCommand }: Props) => {
    const { t } = useTranslation(["modals"]);
    const { classes } = useStyles();

    const policyCommands = group?.policy || [];

    const { updatePolicyCommands } = usePolicyCommands();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const errors = validateEditor(rawEditor);
        if (errors) {
            showErrorToast(t("modals:newPolicyCommand.toast.validationFailed", { message: errors[0]?.message }));
            return;
        }

        const payload = rawEditor && (rawEditor.getValue() as CommandPayload);
        if (!payload) {
            showErrorToast(t("modals:newPolicyCommand.toast.somethingWentWrong"));
            return;
        }

        if (isPolicy && group) {
            const policyPayload = Array.isArray(policyCommands) && [...policyCommands, payload];
            await updatePolicyCommands({ groupId: group.id, policy: JSON.stringify(policyPayload) });
        } else if (createCommand) {
            await createCommand(payload, null);
        }
        toggleDialog();
    };

    useEffect(() => {
        rawEditor = updateEditor(rawEditor, editorRef, JsonEditorCommandType.Raw);

        // destroy the editor to prevent duplicates when component is unmounting
        return () => {
            if (rawEditor) {
                rawEditor.destroy();
            }
        };
    }, [editorRef]);

    return (
        <React.Fragment>
            <form onSubmit={handleSubmit}>
                <div className={classes.sectionContainer}>
                    <InputItemContainer>
                        {/* filled out by jsoneditor */}
                        <div id="json-editor" ref={editorRef}></div>
                        <button ref={submitRef} type="submit" hidden />
                    </InputItemContainer>
                </div>
            </form>
        </React.Fragment>
    );
};
